import axios from "axios";
import { consultationService } from "../services/consultationService";
import type { Constante, VisitReason } from "../types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UseConsultationOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

interface ConsultationData {
  patientId: number;
  doctorId: number;
  constanteId: number;
  symptoms?: string;
  assessment?: string;
  recommendation?: string;
  consultationFee?: number;
  amountPaid?: number;
  amountDue?: number;
}

export const useConsultation = (options?: UseConsultationOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      constante,
      doctorId,
      consultationData,
    }: {
      constante: Constante;
      doctorId: number;
      consultationData?: Partial<ConsultationData>;
    }) => {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Token d'authentification manquant");

      const patientId = constante.patient?.id || constante.patientId;
      if (!patientId || patientId <= 0)
        throw new Error("ID du patient non trouvé");

      const defaultConsultationData: ConsultationData = {
        patientId,
        doctorId,
        constanteId: constante.id,
        symptoms: "Consultation basée sur les constantes médicales",
        assessment: `Examen des constantes: ${[
          constante.weight && `Poids: ${constante.weight}kg`,
          constante.height && `Taille: ${constante.height}m`,
          constante.bloodPressure && `Tension: ${constante.bloodPressure}`,
          constante.heartRate && `Rythme cardiaque: ${constante.heartRate}bpm`,
          constante.temperature && `Température: ${constante.temperature}°C`,
        ]
          .filter(Boolean)
          .join(", ")}`,
        recommendation: "Suivi selon les résultats des constantes médicales",
        consultationFee: consultationData?.consultationFee || 0,
        amountPaid: consultationData?.amountPaid || 0,
        amountDue: consultationData?.amountDue || 0,
        ...consultationData,
      };

      const medicalRecordResponse = await axios.post(
        "/api/medical-records",
        defaultConsultationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const reason =
        (constante.reason as VisitReason) || "GENERAL_CONSULTATION";
      await consultationService.markConstanteAsConsulted(
        constante.id,
        medicalRecordResponse.data.id,
        reason
      );

      return {
        medicalRecordId: medicalRecordResponse.data.id,
        patientName: constante.patientFullName ||
          `${constante.firstName || ""} ${constante.lastName || ""}`.trim(),
      };
    },
    onSuccess: (data) => {
      const successMessage = `Consultation créée avec succès pour ${data.patientName}. Dossier médical #${data.medicalRecordId} enregistré.`;

      // Invalidate the constantes cache to refresh the list
      queryClient.invalidateQueries({ queryKey: ['constantes'] });

      if (options?.onSuccess) {
        options.onSuccess(successMessage);
      }
    },
    onError: (error: any) => {
      console.error("Erreur lors de la création de la consultation:", error);
      let errorMessage = "Erreur lors de la création de la consultation";

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          errorMessage = "Accès non autorisé. Veuillez vous reconnecter.";
        } else if (error.response?.status === 404) {
          errorMessage = "Patient ou constante non trouvé.";
        } else if (error.response?.status === 400) {
          errorMessage = "Données invalides pour la consultation.";
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = `Erreur serveur: ${error.response?.status}`;
        }
      } else {
        errorMessage = error.message || errorMessage;
      }

      if (options?.onError) {
        options.onError(errorMessage);
      }
    },
  });

  const markAsConsulted = async (
    constante: Constante,
    doctorId: number,
    consultationData?: Partial<ConsultationData>
  ): Promise<void> => {
    await mutation.mutateAsync({ constante, doctorId, consultationData });
  };

  return {
    loading: mutation.isPending,
    error: mutation.error ? (mutation.error as any).message : null,
    markAsConsulted,
  };
};

export default useConsultation;

